import axios, { AxiosResponse } from 'axios';
import { ApiResponse, CreateOpportunityRequest, ExploreFilters, LocationCoordinates, LocationSearchParams, OpportunityApplication } from '../types';
import { toggleAvailability } from '../store/slices/userSlice';
import toast from 'react-hot-toast';

// Prioritize environment variable over fallback hardcoded URL for flexible Staging/Prod builds
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://staging.13.204.65.150.nip.io/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    
    // Log to global array for DebugDrawer
    if (typeof window !== 'undefined') {
      (window as any).__NETWORK_LOGS__ = (window as any).__NETWORK_LOGS__ || [];
      const logEntry = {
        type: 'request',
        url: fullUrl || 'URL unknown',
        method: config.method?.toUpperCase() || 'GET',
        data: config.data, // Include Request payload
        timestamp: new Date().toLocaleTimeString(),
      };
      (window as any).__NETWORK_LOGS__.unshift(logEntry); // Add to top
      window.dispatchEvent(new CustomEvent('network_log_updated'));
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and debug logs
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (typeof window !== 'undefined') {
      (window as any).__NETWORK_LOGS__ = (window as any).__NETWORK_LOGS__ || [];
      const logEntry = {
        type: 'success',
        url: response.config.url || 'URL unknown',
        method: response.config.method?.toUpperCase() || 'GET',
        status: response.status,
        data: response.data, // Include Response payload
        timestamp: new Date().toLocaleTimeString(),
      };
      (window as any).__NETWORK_LOGS__.unshift(logEntry);
      window.dispatchEvent(new CustomEvent('network_log_updated'));
    }
    return response;
  },
  (error) => {
    if (typeof window !== 'undefined') {
      (window as any).__NETWORK_LOGS__ = (window as any).__NETWORK_LOGS__ || [];
      const logEntry = {
        type: 'fail',
        url: error.config?.url || 'URL unknown',
        method: error.config?.method?.toUpperCase() || 'GET',
        status: error.response?.status,
        message: error.message || 'Unknown error',
        data: error.response?.data, // Include Error response payload
        timestamp: new Date().toLocaleTimeString(),
      };
      (window as any).__NETWORK_LOGS__.unshift(logEntry);
      window.dispatchEvent(new CustomEvent('network_log_updated'));
    }

    if (error.response?.status === 401) {
      // Token expired or invalid - clear auth and redirect to login
      const token = localStorage.getItem('token');
      const isPublicApi = error.config?.url?.includes('/public/');
      const pathname = window.location.pathname;
      const isPublicPage = pathname.startsWith('/invoice/') || 
                           pathname.startsWith('/crm/contract/') || 
                           pathname.startsWith('/gallery/') || 
                           pathname.startsWith('/explore') ||
                           ['/', '/login', '/register', '/home', '/pricing', '/contact', '/forgot-password', '/reset-password', '/verify-email'].includes(pathname);

      if (token && !isPublicApi && !isPublicPage) {
        // Only redirect if we had a token, it's NOT a public API call, AND we are NOT on a public page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const authAPI = {
  register: (data: any) => api.post('/auth/register', data),
  login: (data: any) => api.post('/auth/login', data),
  refreshToken: async (refreshToken: string) => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
  googleLogin: async (data: { id_token?: string; access_token?: string }) => {
    const response = await api.post('/auth/google', data);
    return response.data;
  },
  verifyEmail: (token: string) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email: string) => api.post('/auth/resend-verification', { email }),
  cancelOutOfZoneSignup: () => api.post('/auth/cancel-out-of-zone-signup'),
};

// export const userAPI = {
//   getProfile: (id?: string) => api.get(`/users/profile${id ? `/${id}` : ''}`),
//   updateProfile: (data: any) => api.put('/users/profile', data),
//   getProfileComplete: () => api.get('/users/profile-complete'),
//   updateProfileComplete: (data: any) => api.put('/users/profile-complete', data),
//   addPortfolioItem: (data: any) => api.post('/users/portfolio', data),
//   uploadPhoto: (type: 'profile' | 'cover', file: FormData) => 
//     api.post(`/users/photo/${type}`, file, {
//       headers: { 'Content-Type': 'multipart/form-data' }
//     }),
// };

// export const opportunitiesAPI = {
//   getNearby: () => api.get('/opportunities/nearby'),
//   getSuggestions: () => api.get('/opportunities/suggestions'),
//   create: (data: any) => api.post('/opportunities', data),
//   apply: (id: string, data: any) => api.post(`/opportunities/${id}/apply`, data),
// };

// export const postsAPI = {
//   getFeed: (page = 1) => api.get(`/posts/feed?page=${page}`),
//   create: (data: any) => api.post('/posts', data),
//   like: (id: string) => api.post(`/posts/${id}/like`),
//   addComment: (id: string, data: any) => api.post(`/posts/${id}/comment`, data),
//   getComments: (id: string) => api.get(`/posts/${id}/comments`),
// };

export const messagesAPI = {
  getConversations: (page = 1, limit = 20, collabFilter?: string) => api.get(`/messages/conversations?page=${page}&limit=${limit}${collabFilter ? `&collab_filter=${collabFilter}` : ''}`),
  getMessageRequests: (page = 1, limit = 20) => api.get(`/messages/requests?page=${page}&limit=${limit}`),
  handleMessageRequest: (conversationId: string, action: 'accept' | 'reject') => 
    api.post(`/messages/requests/${conversationId}`, { action }),
  getMessages: (conversationId: string, page = 1) => 
    api.get(`/messages/conversations/${conversationId}?page=${page}`),
  sendMessage: (data: any) => api.post('/messages/send', data),
  markAsRead: (conversationId: string) => 
    api.put(`/messages/conversations/${conversationId}/read`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  deleteConversation: (conversationId: string) => api.delete(`/messages/conversations/${conversationId}`),
};
  

// Add this to your existing api.ts file
export const notificationAPI = {
    getNotifications: (params: { limit: number; offset: number; loadMore?: boolean }) => api.get(`/notifications?limit=${params.limit}&offset=${params.offset}`),
    markAsRead: (notificationId: string) => api.put(`/notifications/${notificationId}/read`),
    markAllAsRead: () => api.put('/notifications/mark-all-read'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    respondToCollaborationInvite: (data: {
      notification_id: string;
      collaboration_id: string;
      collaborator_id: string;
      action: 'accept' | 'reject';
      message?: string;
    }) => api.post('/notifications/collaboration-invite/respond', data),
    deleteNotification: (notificationId: string) => api.delete(`/notifications/${notificationId}`),
    clearAllNotifications: () => api.delete('/notifications/clear-all'),
  };
  

export const searchAPI = {
  search: (data: any) => api.post('/search', data),
  getTags: () => api.get('/search/tags'),
  getPredefinedTags: () => api.get('/search/tags/predefined'),
};

export const uploadAPI = {
  uploadImage: (file: FormData, onUploadProgress?: (progressEvent: any) => void) => 
    api.post('/upload/image', file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    }),
};

export const postsAPI = {
    getFeed: (page = 1) => api.get(`/posts/feed?page=${page}`),
    create: (data: any) => api.post('/posts', data),
    like: (id: string) => api.post(`/posts/${id}/like`),
    addComment: (id: string, data: any) => api.post(`/posts/${id}/comment`, data),
    getComments: (id: string) => api.get(`/posts/${id}/comments`),
    getUserPosts: (userId: string) => api.get(`/posts/user/${userId}`),
    deletePost: (id: string) => api.delete(`/posts/${id}`),
    sharePost: (id: string) => api.post(`/posts/${id}/share`),
    updatePost: (id: string, data: any) => api.put(`/posts/${id}`, data),
  };
  
  // User API endpoints
export const userAPI = {
    getProfile: (id?: string) => api.get(`/users/profile${id ? `/${id}` : ''}`),
    updateProfile: (data: any) => api.put('/users/profile', data),
    acceptTerms: () => api.post('/users/accept-terms'),
    changePassword: (currentPassword: string, newPassword: string) => 
      api.put('/users/change-password', { current_password: currentPassword, new_password: newPassword }),
    getProfileComplete: () => api.get('/users/profile-complete'),
    updateProfileComplete: (data: any) => api.put('/users/profile-complete', data),
    // addPortfolioItem: (data: any) => api.post('/users/portfolio', data),
    removePortfolioItem: (id: string) => api.delete(`/users/portfolio/${id}`),
    updatePortfolioItem: (id: string, formData: FormData) => 
        api.put(`/users/portfolio/${id}`, formData),    getUserPortfolio: (userId: string, page: number = 1, limit: number = 12, tags?: string[]) => 
      api.get(`/users/${userId}/portfolio`, { 
        params: { 
          page, 
          limit,
          tags: tags?.join(',') 
        } 
      }),
    uploadPhoto: (type: 'profile' | 'cover' | 'business_logo', file: FormData, onUploadProgress?: (progressEvent: any) => void) => 
      api.post(`/users/photo/${type}`, file, { onUploadProgress }),
    getUserCollaborations: (userId: string) => api.get(`/users/${userId}/collaborations`),
    toggleAvailability: (userId: string) => api.patch(`/users/availability/toggle`),
    addPortfolioItem: (formData: FormData, onUploadProgress?: (progressEvent: any) => void) => 
      api.post('/users/portfolio', formData, { onUploadProgress }),
    deletePortfolioItem: (portfolioId: string) => api.delete(`/users/portfolio/${portfolioId}`),
      searchUsers: (query: string) => 
        api.get(`/users/search?q=${encodeURIComponent(query)}`),
      recalculateUserCounts: (userId: string) => 
        api.post(`/users/${userId}/recalculate-counts`),
      blockUser: (blockedId: string) => api.post('/users/block', { blocked_id: blockedId }),
      unblockUser: async (id: string) => {
    const response = await api.delete(`/users/block/${id}`);
    return response.data;
  },

  getBlockedUsers: async () => {
    const response = await api.get('/users/blocked');
    return response.data.data;
  },

  reportUser: async (data: { reported_id: string; reason: string; description: string }) => {
    const response = await api.post('/users/report', data);
    return response.data;
  },

  markReferralSeen: (userId: string) => api.post('/users/referral/seen', { user_id: userId }),

  getNotificationPreferences: () => api.get('/users/notification-preferences'),
  updateNotificationPreferences: (data: { email_notifications: boolean; push_notifications: boolean }) =>
    api.put('/users/notification-preferences', data),
  deleteAccount: () => api.delete('/users/me'),
  getUserPortfolioTags: (userId: string) => api.get(`/users/${userId}/portfolio/tags`),
  getReferrals: () => api.get('/users/referrals'),
  claimReferral: (referralCode: string) => api.post('/users/claim-referral', { referral_code: referralCode }),
  getInstagramFeed: (after?: string) => api.get('/instagram/feed', { params: { after } }),
  getInstagramProfile: (userId?: string) => api.get('/public/instagram/profile', { params: { user_id: userId } }),
  syncInstagram: (mediaIds?: string[], mediaRotations?: Record<string, number>, syncType?: string) => api.post('/instagram/sync', { media_ids: mediaIds, media_rotations: mediaRotations, sync_type: syncType }),
  disconnectInstagram: () => api.post('/instagram/disconnect'),

  // Portfolio Folder APIs
  getPortfolioFolders: (userId: string) => api.get(`/users/${userId}/portfolio/folders`),
  getOwnPortfolioFolders: () => api.get('/users/portfolio/folders'),
  createPortfolioFolder: (data: { name: string; portfolio_ids?: string[] }) => api.post('/users/portfolio/folders', data),
  updatePortfolioFolder: (folderId: string, data: { name: string }) => api.put(`/users/portfolio/folders/${folderId}`, data),
  deletePortfolioFolder: (folderId: string) => api.delete(`/users/portfolio/folders/${folderId}`),
  addItemsToFolder: (folderId: string, portfolioIds: string[]) => api.post(`/users/portfolio/folders/${folderId}/items`, { portfolio_ids: portfolioIds }),
  removeItemFromFolder: (folderId: string, portfolioId: string) => api.delete(`/users/portfolio/folders/${folderId}/items/${portfolioId}`),
  getFolderItems: (userId: string, folderId: string, page: number = 1, limit: number = 12) => api.get(`/users/${userId}/portfolio/folders/${folderId}/items`, { params: { page, limit } }),
  getOwnFolderItems: (folderId: string, page: number = 1, limit: number = 12) => api.get(`/users/portfolio/folders/${folderId}/items`, { params: { page, limit } }),
};

  export const portfolioAPI = {
    getPortfolioItem: (id: string) => api.get(`/portfolio/${id}`),
    toggleLike: (portfolioId: string) => api.post(`/portfolio/${portfolioId}/like`),
    addComment: (portfolioId: string, content: string) => 
      api.post(`/portfolio/${portfolioId}/comments`, { content }),
    getComments: (portfolioId: string, userId: string) => api.get(`/portfolio/${portfolioId}/comments?user_id=${userId}`),
    incrementViews: (portfolioId: string) => api.post(`/portfolio/${portfolioId}/views`),
    incrementShares: (portfolioId: string) => api.post(`/portfolio/${portfolioId}/shares`),
    addToCollection: (portfolioId: string) => 
        api.post(`/portfolio/${portfolioId}/collection`),
      
      setAsPhoto: (portfolioId: string, type: 'profile' | 'cover') => 
        api.post(`/portfolio/${portfolioId}/set-as-photo`, { type }),
      
      reportContent: (portfolioId: string, reason: string) => 
        api.post(`/portfolio/${portfolioId}/report`, { reason }),
      checkLiked: (portfolioId: string) => api.get(`/portfolio/${portfolioId}/liked`),
      // Comment interactions
      toggleCommentLike: (commentId: string) => 
        api.post(`/portfolio/comments/${commentId}/like`),
      
      addCommentReply: (commentId: string, content: string) =>
        api.post(`/portfolio/comments/${commentId}/reply`, { content }),
        
      getCommentReplies: (commentId: string, userId: string) =>
        api.get(`/portfolio/comments/${commentId}/replies?user_id=${userId}`),
      
      getPortfolioLikes: (id: string, page = 1) => api.get(`/portfolio/${id}/likes?page=${page}`),
  };
  
  
  // Opportunities API endpoints
//   export const opportunitiesAPI = {
//     getNearby: () => api.get('/opportunities/nearby'),
//     getSuggestions: () => api.get('/opportunities/suggestions'),
//     create: (data: any) => api.post('/opportunities', data),
//     apply: (id: string, data: any) => api.post(`/opportunities/${id}/apply`, data),
//     getDetails: (id: string) => api.get(`/opportunities/${id}`),
//     getUserOpportunities: () => api.get('/opportunities/my'),
//     getMyApplications: () => api.get('/applications/my'),
//     updateApplicationStatus: (id: string, status: string) => 
//       api.put(`/applications/${id}/status`, { status }),
//     update: (id: string, data: any) => api.put(`/opportunities/${id}`, data),
//     delete: (id: string) => api.delete(`/opportunities/${id}`),
//     toggleStatus: (id: string) => api.put(`/opportunities/${id}/toggle`),
//     getApplications: (id: string) => api.get(`/opportunities/${id}/applications`),
//     toggleBookmark: (id: string) => api.post(`/opportunities/${id}/bookmark`),
//   };

export const exploreAPI = {
    // Get explore users with filters
    getExploreUsers: (filters?: ExploreFilters) => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              // Handle arrays by appending each value with []
              value.forEach(item => {
                params.append(`${key}[]`, item.toString());
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }
      return api.get(`/explore/users?${params.toString()}`);
    },
    // // Get filter options
    // getFilterOptions: () => api.get('/explore/filters'),

    // Get filter options
  getFilterOptions: () => api.get('/explore/filters'),

  // Filter presets management
  saveFilterPreset: (data: { name: string; filters: ExploreFilters }) => 
    api.post('/explore/filter-presets', data),

  getFilterPresets: () => 
    api.get('/explore/filter-presets'),

  deleteFilterPreset: (presetId: string) => 
    api.delete(`/explore/filter-presets/${presetId}`),

  // Filter history
  getFilterHistory: () => 
    api.get('/explore/filter-history'),

  saveFilterHistory: (data: { filters: ExploreFilters; result_count: number }) => 
    api.post('/explore/filter-history', data),
  };
  

export const opportunityAPI = {
    // Create new opportunity
    createOpportunity: (formData: FormData) => 
        api.post('/opportunities', formData),
    
    // Get opportunities with filters
    getOpportunities: (params?: {
      page?: number;
      limit?: number;
      type?: string;
      location?: string;
      tags?: string;
      search?: string;
      experience?: string;
      compensation?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }
      return api.get(`/opportunities?${queryParams.toString()}`);
    },
    
    // Get specific opportunity
    getOpportunity: (id: string) => 
      api.get(`/opportunities/${id}`),
    
    // Get user's opportunities
    getMyOpportunities: (status?: string) => 
      api.get(`/opportunities/my${status ? `?status=${status}` : ''}`),
    
    // Apply to opportunity
    applyToOpportunity: (id: string, data: {
      message?: string;
      portfolio_items?: string[];
    }) => api.post(`/opportunities/${id}/apply`, data),
    
    // Update application status (for opportunity creators)
    updateApplicationStatus: (applicationId: string, data: {
      status: OpportunityApplication['status'];
      message?: string;
    }) => api.put(`/opportunities/applications/${applicationId}/status`, data),
    
    // Update opportunity
    // updateOpportunity: (id: string, data: Partial<CreateOpportunityRequest>) => 
    //   api.put(`/opportunities/${id}`, data),
    
    // Delete opportunity
    deleteOpportunity: (id: string) => 
      api.delete(`/opportunities/${id}`),

    // Upload opportunity images
    uploadOpportunityImages: (files: FormData) => 
      api.post('/opportunities/upload-images', files),

      // Get applications for an opportunity (for creators)
  getOpportunityApplications: (opportunityId: string, page = 1, limit = 20) =>
    api.get(`/opportunities/${opportunityId}/applications?page=${page}&limit=${limit}`),

  // Bulk reject applications
  bulkRejectApplications: (opportunityId: string, data: {
    application_ids: string[];
    message?: string;
  }) => api.post(`/opportunities/${opportunityId}/applications/bulk-reject`, data),

  updateOpportunity: (id: string, formData: FormData) => 
    api.put(`/opportunities/${id}`, formData),

  // Track opportunity view
  trackView: (id: string) => 
    api.post(`/opportunities/${id}/view`),

  // Subscription management
  toggleSubscription: (id: string, data: {
    subscription_type: string;
    subscribe: boolean;
  }) => api.post(`/opportunities/${id}/subscribe`, data),

  getSubscriptionStatus: (id: string) => 
    api.get(`/opportunities/${id}/subscription-status`),

  // Withdraw application
  withdrawApplication: (id: string) => api.delete(`/opportunities/${id}/application`),
  getMyApplications: () => api.get('/opportunities/my-applications'),
  
  // Revive opportunity
  reviveOpportunity: (id: string) => api.post(`/opportunities/${id}/revive`),

  // Close opportunity
  closeOpportunity: (id: string) => api.post(`/opportunities/${id}/close`),
  };
  
  
  export const reviewsAPI = {
    createReview: (data: any) => api.post('/reviews', data),
    getUserReviews: (userId: string, page = 1) => api.get(`/reviews/user/${userId}?page=${page}`),
    getMyReviews: (params?: { booking_id?: string }) => api.get('/reviews/my', { params }),
    updateReview: (id: string, data: any) => api.put(`/reviews/${id}`, data),
    deleteReview: (id: string) => api.delete(`/reviews/${id}`),
  };
  
  export const collaborationAPI = {
    // Get all collaborations with filters
    getAllCollaborations: (params?: {
      status?: string;
      location?: string;
      budget_min?: number;
      budget_max?: number;
      page?: number;
      limit?: number;
    }) => {
      const queryString = params ? '?' + new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)])
      ).toString() : '';
      
      return api.get(`/collaborations${queryString}`);
    },
    
    // Get user's collaborations
    getUserCollaborations: (userId: string) => 
      api.get(`/collaborations/user/${userId}`),
    
    // Get single collaboration
    getCollaboration: (id: string) => 
      api.get(`/collaborations/${id}`),
    
    // Create new collaboration
    createCollaboration: (data: FormData, onUploadProgress?: (progressEvent: any) => void) => 
      api.post('/collaborations', data, { onUploadProgress }),
    
    // Update collaboration
    updateCollaboration: (id: string, data: FormData, onUploadProgress?: (progressEvent: any) => void) => 
      api.put(`/collaborations/${id}`, data, { onUploadProgress }),
    
    // Delete collaboration
    deleteCollaboration: (id: string) => 
      api.delete(`/collaborations/${id}`),
    
    // Apply to collaboration
    applyToCollaboration: (data: {
      collaboration_id: string;
      message: string;
      portfolio_items?: string[];
    }) => api.post('/collaborations/apply', data),
    
    // Get collaboration applications
    getCollaborationApplications: (collaborationId: string) => 
      api.get(`/collaborations/${collaborationId}/applications`),

    getCollaborators: (collaborationId: string) => 
        api.get(`/collaborations/${collaborationId}/collaborators`),
      
      uploadPhotos: (collaborationId: string, formData: FormData) => 
        api.post(`/collaborations/${collaborationId}/upload`, formData),
      
      submitApproval: (collaborationId: string, data: {
        approved: boolean;
        insights: string;
      }) => 
        api.post(`/collaborations/${collaborationId}/approve`, data),
      
      publishCollaboration: (collaborationId: string) => 
        api.post(`/collaborations/${collaborationId}/publish`),
      
      acceptApplication: (applicationId: string, data: { role: string }) => 
        api.post(`/collaborations/applications/${applicationId}/accept`, data),
      
      getAll: (params?: any) => {
        const queryString = params ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString() : '';
        
        return api.get(`/collaborations${queryString}`);
      },
      
      getByUser: (userId: string) => 
        api.get(`/collaborations/user/${userId}`),
      
      create: (formData: FormData) => 
        api.post('/collaborations', formData),
      
      update: (id: string, formData: FormData) => 
        api.put(`/collaborations/${id}`, formData),
      
      delete: (id: string) => 
        api.delete(`/collaborations/${id}`),
      
      apply: (data: {
        collaboration_id: string;
        message: string;
        portfolio_items?: string[];
      }) => api.post('/collaborations/apply', data),
      
      getApplications: (collaborationId: string) => 
        api.get(`/collaborations/${collaborationId}/applications`),

      sendCollaborationRequest: (data: {
        receiver_id: string;
        type: string;
        timeline: string;
        subject: string;
        message: string;
        location?: string;
        budget?: number;
        is_paid: boolean;
        duration?: string;
        urgency?: string;
        requirements?: string;
        deliverables?: string;
        equipment_needed?: string;
        preferred_dates?: string[];
      }) => api.post('/collaboration-requests', data),
    
      getCollaborationRequests: (params?: {
        collab_type?: string;
        status?: string;
        page?: number;
        limit?: number;
        is_paid?: boolean;
        type?: 'sent' | 'received' | 'all';
        service_package_id?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return api.get(`/collaboration-requests?${queryParams.toString()}`);
      },
    
      getCollaborationRequest: (id: string) =>
        api.get(`/collaboration-requests/${id}`),
    
      respondToCollaborationRequest: (requestId: string, data: {
        status: 'accepted' | 'declined';
        response_message?: string;
      }) => api.put(`/collaboration-requests/${requestId}`, data),
    
      getNotifications: (unreadOnly?: boolean) => 
        api.get(`/collaboration-requests/notifications${unreadOnly ? '?unread_only=true' : ''}`),
    
      markNotificationAsRead: (notificationId: string) => 
        api.put(`/collaboration-requests/notifications/${notificationId}/read`),

      markAllNotificationsAsRead: () => 
        api.put('/collaboration-requests/notifications/read-all'),
      
      withdrawCollaborationRequest: (requestId: string) => 
        api.put(`/collaboration-requests/${requestId}/withdraw`),
        
      completeCollaborationRequest: (requestId: string) => 
        api.post(`/collaboration-requests/${requestId}/complete`),
        
      revokeCompleteCollaborationRequest: (requestId: string) => 
        api.post(`/collaboration-requests/${requestId}/revoke-complete`),
        
      cancelCollaborationRequest: (requestId: string, data: { reason: string; details?: string }) => 
        api.post(`/collaboration-requests/${requestId}/cancel`, data),
      
      getInviteDetails: (token: string) => 
        api.get(`/collaborations/invites/${token}`),
  };

  export const dashboardAPI = {
    getDashboardData: () => api.get('/dashboard/data'),
    updateAvailability: (isAvailable: boolean) => 
      api.post('/dashboard/update-availability', { is_available: isAvailable }),
    
    // New endpoints
    getDetailedPerformance: () => api.get('/dashboard/performance/details'),
    sendCollaborationRequest: (data: {
      receiver_id: string;
      type: string;
      timeline: string;
      description: string;
      location?: string;
      budget?: number;
      is_paid: boolean;
    }) => api.post('/collaboration-requests', data),
    
    getCollaborationRequests: (params?: { 
    status?: string; 
    limit?: number; 
    page?: number;
    type?: string;
    is_paid?: boolean;
  }) => 
    api.get(`/collaboration-requests`, { params }),
    
    respondToCollaborationRequest: (requestId: string, data: {
      status: 'accepted' | 'declined' | 'negotiating';
      response_message?: string;
    }) => api.put(`/collaboration-requests/${requestId}`, data),

    // New Phase 2 endpoints
    getPersonalizedRecommendations: () => api.get('/recommendations/personalized'),
    submitRecommendationFeedback: (data: {
      recommendation_id: string;
      action: 'like' | 'dislike' | 'view' | 'apply';
      rating?: number;
    }) => api.post('/recommendations/feedback', data),
    
    getRecentPortfolioUpdates: () => api.get('/portfolio/recent'),
    uploadPortfolioItem: (data: FormData) => api.post('/portfolio/upload', data),
  };

  export const memoryBoardAPI = {
    search: (params?: {
      q?: string;
      lat?: number;
      lng?: number;
      radius?: number;
      type?: string;
      page?: number;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }
      return api.get(`/memory-board/search?${queryParams.toString()}`);
    }
  };

  export const genericAPIs = {
    getUserRoleSkills: (role: string) => api.get(`/skills/${role}`),
  
  // Video APIs
  getUserVideos: (userId: string) => api.get(`/users/${userId}/videos`),
  addVideo: (data: { youtube_url: string; title: string; description: string; tags: string[] }) => api.post('/users/videos', data),
  initiateProfileVideoUpload: (data: { filename: string; size: number; content_type: string }) => api.post('/users/videos/cloud-upload/initiate', data),
  completeProfileVideoUpload: (data: { file_id: string; filename: string; title?: string; description?: string; tags?: string[]; thumbnail?: string }) => api.post('/users/videos/cloud-upload/complete', data),
  initiateProjectVideoUpload: (data: { filename: string; size: number; content_type: string }) => api.post('/collaborations/videos/cloud-upload/initiate', data),
  completeProjectVideoUpload: (data: { file_id: string; filename: string }) => api.post('/collaborations/videos/cloud-upload/complete', data),
  deleteVideo: (videoId: string) => api.delete(`/users/videos/${videoId}`),
  updateVideo: (videoId: string, data: { title: string; description: string; tags: string[] }) => api.put(`/users/videos/${videoId}`, data),
  batchDeletePortfolioItems: (ids: string[]) => api.post('/users/portfolio/batch-delete', { ids }),
};  
  
  
  
  
  export const userRelationshipAPI = {
    // Get user followers
    getUserFollowers: (userId: string, params?: {
      page?: number;
      limit?: number;
      search?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }
      return api.get(`/users/${userId}/followers?${queryParams.toString()}`);
    },
  
    // Get user following
    getUserFollowing: (userId: string, params?: {
      page?: number;
      limit?: number;
      search?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }
      return api.get(`/users/${userId}/following?${queryParams.toString()}`);
    },
  
    // Get user collaborators
    getUserCollaborators: (userId: string, params?: {
      page?: number;
      limit?: number;
      search?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }
      return api.get(`/users/${userId}/collaborators?${queryParams.toString()}`);
    },
  
    // Toggle follow/unfollow
    toggleFollow: (userId: string) => 
      api.post(`/users/${userId}/follow`),
  
    // Check follow status
    checkFollowStatus: (userId: string) => 
      api.get(`/users/${userId}/follow-status`),
  };
  
  // Location APIs
export const locationAPI = {
  // Update user location
  updateUserLocation: (data: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    location?: string;
  }) => api.post('/location/user', data),

  // Update opportunity location
  updateOpportunityLocation: (opportunityId: string, data: {
    latitude: number;
    longitude: number;
    location_radius?: number;
    location?: string;
  }) => api.post(`/location/opportunities/${opportunityId}`, data),

  // Get nearby opportunities
  getNearbyOpportunities: (params?: LocationSearchParams) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return api.get(`/location/nearby-opportunities?${queryParams.toString()}`);
  },

  // Get nearby users
  getNearbyUsers: (params?: LocationSearchParams) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return api.get(`/location/nearby-users?${queryParams.toString()}`);
  },

  // Get current location (browser geolocation)
  getCurrentLocation: (): Promise<LocationCoordinates> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          // console.log(window.google)
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            const latlng = { lat: latitude, lng: longitude };
            
            geocoder.geocode({ location: latlng }, (results: any[], status: string) => {    
              if (status === 'OK' && results && results[0]) {
                const locationName = results[0].formatted_address;
                resolve({
                  latitude: latitude,
                  longitude: longitude,
                  accuracy: accuracy,
                  name: locationName
                });
              } else {
                resolve({
                  latitude: latitude,
                  longitude: longitude,
                  accuracy: accuracy
                });
              }
            });
          } 
          else {
            resolve({
              latitude: latitude,
              longitude: longitude,
              accuracy: accuracy
            });
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  },
};

export const collaborationService = {
  submitApproval: (id: string, approved: boolean, insights: string) => 
    api.post(`/collaborations/${id}/approve`, { approved, insights }),
};

export const reviewAPI = {
  createReview: (data: { reviewee_id: string; booking_id: string; rating: number; comment: string }[]) => 
    api.post('/reviews', data),
};

export const waitlistAPI = {
  subscribeToApp: (email: string) => api.post('/waitlist/app', { email }),
};

export const showcaseAPI = {
  getUserShowcase: (userId: string) => api.get(`/users/${userId}/showcase`),
  createSection: (data: { title: string }) => api.post('/showcase/sections', data),
  updateSection: (id: string, data: { title?: string; order?: number }) => api.put(`/showcase/sections/${id}`, data),
  deleteSection: (id: string) => api.delete(`/showcase/sections/${id}`),
  createPage: (data: { section_id: string; title: string; layout_type: 'grid' | 'canvas' }) => api.post('/showcase/pages', data),
  updatePage: (id: string, data: { title?: string; layout_type?: 'grid' | 'canvas'; order?: number }) => api.put(`/showcase/pages/${id}`, data),
  deletePage: (id: string) => api.delete(`/showcase/pages/${id}`),
  createItem: (data: any) => api.post('/showcase/items', data),
  updateItem: (id: string, data: any) => api.put(`/showcase/items/${id}`, data),
  deleteItem: (id: string) => api.delete(`/showcase/items/${id}`),
  bulkUpdateItems: (pageId: string, items: any[]) => api.put(`/showcase/pages/${pageId}/items/bulk`, { items }),
};
  // Analytics API
export const analyticsAPI = {
  recordImpressions: (userIds: string[]) => 
    api.post('/analytics/impressions', { user_ids: userIds }),
};

export const crmAPI = {
  // Leads
  getLeads: () => api.get('/crm/leads'),
  createLead: (data: any) => api.post('/crm/leads', data),
  updateLead: (id: string, data: any) => api.put(`/crm/leads/${id}`, data),
  updateLeadStatus: (id: string, status: string) => api.put(`/crm/leads/${id}/status`, { status }),
  deleteLead: (id: string) => api.delete(`/crm/leads/${id}`),
  acknowledgeProjectUpdates: (id: string) => api.post(`/crm/projects/${id}/acknowledge`),
  confirmPortalLead: (id: string) => api.post(`/crm/leads/${id}/confirm`),
  declinePortalLead: (id: string) => api.post(`/crm/leads/${id}/decline`),
  getPortalLead: (id: string) => api.get(`/crm/portal/leads/${id}`),
  getPublicLead: (id: string, token: string) => api.get(`/public/crm/leads/${id}?token=${token}`),

  // Clients
  getClients: () => api.get('/crm/clients'),
  createClient: (data: any) => api.post('/crm/clients', data),
  deleteClient: (id: string) => api.delete(`/crm/clients/${id}`),

  // Projects
  getProjects: () => api.get('/crm/projects'),
  checkCancelledProjects: () => api.get('/crm/projects/check-cancelled'),
  dismissCancelledCollabAlert: (id: string) => api.post(`/crm/projects/${id}/dismiss-cancelled-alert`),
  dismissCompletedCollabAlert: (id: string) => api.post(`/crm/projects/${id}/dismiss-completed-alert`),
  createProject: (data: any) => api.post('/crm/projects', data),
  getProject: (id: string) => api.get(`/crm/projects/${id}`),
  updateProject: (id: string, data: any) => api.put(`/crm/projects/${id}`, data),
  updateProjectStatus: (id: string, status: string, final_amount?: number) => api.put(`/crm/projects/${id}/status`, { status, final_amount }),
  updateProjectConfig: (id: string, data: any) => api.put(`/crm/projects/${id}/config`, data),
  updateProjectChecklist: (id: string, checklist: string) => api.put(`/crm/projects/${id}/checklist`, { checklist }),
  deleteProject: (id: string) => api.delete(`/crm/projects/${id}`),
  deleteContract: (id: string) => api.delete(`/crm/contracts/${id}`),
  getActivities: (id: string) => api.get(`/crm/activities/${id}`),

  // Gallery
  getGalleryImages: (id: string) => api.get(`/crm/projects/${id}/gallery`),
  uploadGalleryImages: (id: string, formData: FormData, config?: any) => 
    api.post(`/crm/projects/${id}/gallery`, formData, config),
  deleteGalleryImage: (projectId: string, imageId: string, deleteFromDrive?: boolean) => 
    api.delete(`/crm/projects/${projectId}/gallery/${imageId}${deleteFromDrive ? '?delete_from_drive=true' : ''}`),
  toggleImageSelection: (imageId: string) => api.put(`/crm/gallery/selection/${imageId}`),

  // Google Drive Cloud Upload
  getGoogleDriveAuthURL: () => api.get('/crm/oauth/google-drive'),
  getGoogleDriveStatus: () => api.get('/crm/oauth/google-drive/status'),
  initiateCRMVideoUpload: (projectId: string, data: { filename: string; size: number; content_type: string }) =>
    api.post(`/crm/projects/${projectId}/gallery/cloud-upload/initiate`, data),
  completeCRMVideoUpload: (projectId: string, data: { file_id: string; filename: string; thumbnail?: string }) =>
    api.post(`/crm/projects/${projectId}/gallery/cloud-upload/complete`, data),
  addCRMGalleryVideoLink: (projectId: string, data: { video_url: string; title?: string; description?: string }) =>
    api.post(`/crm/projects/${projectId}/gallery/video-link`, data),

  // Invoices
  createInvoice: (data: any) => api.post('/crm/invoices', data),
  getProjectInvoices: (projectId: string) => api.get(`/crm/projects/${projectId}/invoices`),
  updateInvoiceStatus: (id: string, status: string) => api.put(`/crm/invoices/${id}/status`, { status }),
  deleteInvoice: (id: string) => api.delete(`/crm/invoices/${id}`),

	  // Contracts
	  getInvoices: () => api.get('/crm/invoices'),
  getContracts: () => api.get('/crm/contracts'),

	  // Public
	  getPublicProject: (id: string, password?: string) => api.get(`/public/crm/projects/${id}${password ? `?password=${password}` : ''}`),
  getPublicGalleryImages: (id: string, password?: string) => api.get(`/public/crm/projects/${id}/gallery${password ? `?password=${password}` : ''}`),
  submitGallerySelection: (id: string, password?: string) => api.post(`/public/crm/projects/${id}/gallery/submit${password ? `?password=${password}` : ''}`),
  getPublicInvoice: (id: string) => api.get(`/public/crm/invoices/${id}`),
  getPublicContract: (id: string, password?: string) => api.get(`/public/crm/contracts/${id}${password ? `?password=${password}` : ''}`),
  acceptPublicInvoice: (id: string, password?: string) => api.post(`/public/crm/invoices/${id}/accept${password ? `?password=${password}` : ''}`),
  rejectPublicInvoice: (id: string, reason: string, password?: string) => api.post(`/public/crm/invoices/${id}/reject${password ? `?password=${password}` : ''}`, { reason }),
  getAcceptedCollaborations: () => api.get('/crm/collaborations'),
  searchPortalUsers: (query: string) => api.get(`/explore/users?search=${query}`),

  // Contracts & Templates
  createContractTemplate: (data: any) => api.post('/crm/contract-templates', data),
  getContractTemplates: () => api.get('/crm/contract-templates'),
  updateContractTemplate: (id: string, data: any) => api.put(`/crm/contract-templates/${id}`, data),
  deleteContractTemplate: (id: string) => api.delete(`/crm/contract-templates/${id}`),
  createContract: (projectId: string, data: any) => api.post(`/crm/projects/${projectId}/contracts`, data),
  getProjectContracts: (projectId: string) => api.get(`/crm/projects/${projectId}/contracts`),
  signContract: (id: string, signatureName: string) => api.post(`/public/crm/contracts/${id}/sign`, { signature_name: signatureName }),
  rejectContract: (id: string, reason: string) => api.post(`/public/crm/contracts/${id}/reject`, { reason }),
  signAsPhotographer: (id: string, signatureName: string) => api.post(`/crm/contracts/${id}/sign-photographer`, { signature_name: signatureName }),
  updateContractStatus: (id: string, status: string) => api.put(`/crm/contracts/${id}/status`, { status }),
  exportCRMData: () => api.get('/crm/export', { responseType: 'blob' }),

  // Client Portal
  getClientPortalData: () => api.get('/crm/portal/my-projects'),
  syncToCollaboration: (id: string) => api.post(`/crm/projects/${id}/sync-collaboration`),

  // Calendar Events
  getCalendarEvents: () => api.get('/crm/calendar-events'),
  createCalendarEvent: (data: any) => api.post('/crm/calendar-events', data),
  updateCalendarEvent: (id: string, data: any) => api.put(`/crm/calendar-events/${id}`, data),
  deleteCalendarEvent: (id: string) => api.delete(`/crm/calendar-events/${id}`),
};

export const subscriptionAPI = {
  getUserUsage: () => api.get('/users/usage/subscription'),
  initializePayment: (data: { plan_tier: string; billing_cycle: string }) => 
    api.post('/subscription/initialize', data),
  verifyPayment: (data: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => 
    api.post('/subscription/verify', data),
  getSubscriptionHistory: () => api.get('/subscription/history'),
  getSubscriptionInvoice: (id: string) => api.get(`/subscription/invoice/${id}`),
};


export const adminAPI = {
  getMetrics: () => api.get('/admin/metrics'),
  getRewards: () => api.get('/admin/rewards'),
  upsertReward: (data: any) => api.post('/admin/rewards', data),
  deleteReward: (id: string) => api.delete(`/admin/rewards/${id}`),
  getTickets: () => api.get('/admin/tickets'),
  updateTicketStatus: (id: string, status: string) => api.put(`/admin/tickets/${id}/status`, { status }),
  resolveUnblockTicket: (id: string, action: 'unblock' | 'reject', adminResponse?: string) =>
    api.post(`/admin/tickets/${id}/resolve-unblock`, { action, admin_response: adminResponse }),
  getUsers: () => api.get('/admin/users'),
  toggleEarlyAccess: (userId: string, hasEarlyAccess: boolean) => 
    api.put(`/admin/users/${userId}/early-access`, { has_early_access: hasEarlyAccess }),
  toggleDisableUser: (userId: string, isDisabled: boolean, reason?: string) => 
    api.put(`/admin/users/${userId}/disable`, { is_disabled: isDisabled, reason }),
  bulkDisableUsers: (userIds: string[], isDisabled: boolean, reason?: string) =>
    api.post('/admin/users/bulk-disable', { user_ids: userIds, is_disabled: isDisabled, reason }),
  getExpansionInterests: () => api.get('/admin/expansion-interests'),
  getEarlyAccessWhitelist: () => api.get('/admin/early-access'),
  addToEarlyAccessWhitelist: (email: string) => api.post('/admin/early-access', { email }),
  removeFromEarlyAccessWhitelist: (id: string) => api.delete(`/admin/early-access/${id}`),
  sendInstagramSyncLaunchEmail: () => api.post('/admin/send-bangalore-focus-launch'),
};

export const supportAPI = {
  submitTicket: (data: { subject: string; message: string }) => api.post('/support/ticket', data),
  createUnblockTicket: (data: { subject: string; message: string }) => api.post('/support/unblock-ticket', data),
  submitExpansionInterest: (data: { email: string; city?: string; state?: string; country?: string; latitude?: number; longitude?: number }) =>
    api.post('/public/expansion-interest', data),
};
